import React, { useState, useEffect } from 'react';
import PageLayout from '../../components/ui/PageLayout';
import FunnelStatsCards from './components/FunnelStatsCards';
import FunnelTable from './components/FunnelTable';
import CreateFunnelModal from './components/CreateFunnelModal';
import FunnelDetailsModal from './components/FunnelDetailsModal';
import FunnelFilters from './components/FunnelFilters';
import BulkActionsBar from './components/BulkActionsBar';
import Button from '../../components/ui/Button';
import { funnelService } from '../../services/funnelService';
import { SkeletonTable, SkeletonMetricCard } from '../../components/ui/SkeletonLoader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';



const FunnelManagement = () => {
  const { user } = useAuth();
  const [funnels, setFunnels] = useState([]);
  const [filteredFunnels, setFilteredFunnels] = useState([]);
  const [selectedFunnels, setSelectedFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all',
    dateRange: '30',
    searchQuery: ''
  });
  const navigate = useNavigate();

  // Load funnels on component mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadFunnels();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  // Apply filters when funnels or filters change
  useEffect(() => {
    applyFilters();
  }, [funnels, filters]);

  const loadFunnels = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await funnelService?.getAllFunnels(user?.id);
      setFunnels(data || []);
    } catch (err) {
      console.error('Error loading funnels:', err);
      setError('Failed to load funnels. Please try again.');
      setFunnels([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...funnels];

    // Status filter
    if (filters?.status !== 'all') {
      filtered = filtered?.filter(funnel => funnel?.status === filters?.status);
    }

    // Type filter
    if (filters?.type !== 'all') {
      filtered = filtered?.filter(funnel => funnel?.type === filters?.type);
    }

    // Search query filter
    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(funnel =>
        funnel?.name?.toLowerCase()?.includes(query) ||
        funnel?.description?.toLowerCase()?.includes(query)
      );
    }

    // Date range filter
    if (filters?.dateRange !== 'all') {
      const days = parseInt(filters?.dateRange);
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - days);

      filtered = filtered?.filter(funnel => {
        const createdDate = new Date(funnel?.created_at);
        return createdDate >= cutoffDate;
      });
    }

    setFilteredFunnels(filtered);
  };

  const handleCreateFunnel = async (funnelData) => {
    try {
      const newFunnel = await funnelService?.createFunnel({ ...funnelData, user_id: user?.id });
      setFunnels(prev => [newFunnel, ...prev]);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating funnel:', error);
      throw error;
    }
  };

  const handleUpdateFunnel = async (funnelId, updates) => {
    try {
      const updatedFunnel = await funnelService?.updateFunnel(funnelId, updates);
      setFunnels(prev => prev?.map(funnel =>
        funnel?.id === funnelId ? updatedFunnel : funnel
      ));
    } catch (error) {
      console.error('Error updating funnel:', error);
      throw error;
    }
  };

  const handleDeleteFunnel = async (funnelId) => {
    try {
      await funnelService?.deleteFunnel(funnelId);
      setFunnels(prev => prev?.filter(funnel => funnel?.id !== funnelId));
      setSelectedFunnels(prev => prev?.filter(id => id !== funnelId));
    } catch (error) {
      console.error('Error deleting funnel:', error);
      throw error;
    }
  };

  const handleBulkAction = async (action, funnelIds) => {
    try {
      switch (action) {
        case 'delete':
          await Promise.all(funnelIds?.map(id => funnelService?.deleteFunnel(id)));
          setFunnels(prev => prev?.filter(funnel => !funnelIds?.includes(funnel?.id)));
          break;
        case 'activate':
          await Promise.all(funnelIds?.map(id => funnelService?.updateFunnel(id, { status: 'active' })));
          setFunnels(prev => prev?.map(funnel =>
            funnelIds?.includes(funnel?.id) ? { ...funnel, status: 'active' } : funnel
          ));
          break;
        case 'pause':
          await Promise.all(funnelIds?.map(id => funnelService?.updateFunnel(id, { status: 'paused' })));
          setFunnels(prev => prev?.map(funnel =>
            funnelIds?.includes(funnel?.id) ? { ...funnel, status: 'paused' } : funnel
          ));
          break;
        default:
          break;
      }
      setSelectedFunnels([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
      throw error;
    }
  };

  const handleFunnelSelect = (funnelId, isSelected) => {
    setSelectedFunnels(prev =>
      isSelected
        ? [...prev, funnelId]
        : prev?.filter(id => id !== funnelId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedFunnels(isSelected ? filteredFunnels?.map(f => f?.id) : []);
  };

  const handleViewDetails = (funnel) => {
    setSelectedFunnel(funnel);
    setIsDetailsModalOpen(true);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  return (
    <PageLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Funnel Management</h1>
          <p className="text-muted-foreground mt-2">
            Create, manage, and optimize your sales funnels
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setIsCreateModalOpen(true)}
          data-create-funnel
        >
          Create Funnel
        </Button>
      </div>
      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[...Array(4)]?.map((_, i) => <SkeletonMetricCard key={i} />)}
        </div>
      ) : (
        <FunnelStatsCards
          funnels={funnels}
          loading={loading}
          summary={{
            total: funnels?.length,
            active: funnels?.filter(f => f?.status === 'active')?.length,
            paused: funnels?.filter(f => f?.status === 'paused')?.length,
            draft: funnels?.filter(f => f?.status === 'draft')?.length
          }}
        />
      )}
      {/* Filters */}
      <FunnelFilters
        filters={filters}
        onFiltersChange={handleFilterChange}
        onClose={() => setFilters({
          status: 'all',
          type: 'all',
          dateRange: '30',
          searchQuery: ''
        })}
      />
      {/* Bulk Actions Bar */}
      {selectedFunnels?.length > 0 && (
        <BulkActionsBar
          selectedCount={selectedFunnels?.length}
          onBulkAction={(action) => handleBulkAction(action, selectedFunnels)}
          onClearSelection={() => setSelectedFunnels([])}
          onStatusChange={(status) => handleBulkAction(status, selectedFunnels)}
          onDelete={() => handleBulkAction('delete', selectedFunnels)}
          onClear={() => setSelectedFunnels([])}
        />
      )}
      {/* Funnel Table */}
      {loading ? (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <SkeletonTable rows={5} cols={7} />
        </div>
      ) : (
        <FunnelTable
          funnels={filteredFunnels}
          loading={loading}
          selectedFunnels={selectedFunnels}
          onFunnelSelect={handleFunnelSelect}
          onSelectAll={handleSelectAll}
          onSelectFunnel={handleFunnelSelect}
          onViewDetails={handleViewDetails}
          onUpdateFunnel={handleUpdateFunnel}
          onDeleteFunnel={handleDeleteFunnel}
          onStatusChange={(funnelId, status) => handleUpdateFunnel(funnelId, { status })}
          onDuplicate={(funnelId) => {
            const funnel = funnels?.find(f => f?.id === funnelId);
            if (funnel) handleCreateFunnel({ ...funnel, name: `${funnel?.name} (Copy)` });
          }}
          onDelete={handleDeleteFunnel}
        />
      )}
      {/* Modals */}
      {isCreateModalOpen && (
        <CreateFunnelModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onCreateFunnel={handleCreateFunnel}
          onSubmit={handleCreateFunnel}
        />
      )}
      {isDetailsModalOpen && selectedFunnel && (
        <FunnelDetailsModal
          isOpen={isDetailsModalOpen}
          funnel={selectedFunnel}
          onClose={() => {
            setIsDetailsModalOpen(false);
            setSelectedFunnel(null);
          }}
          onUpdateFunnel={handleUpdateFunnel}
          onDeleteFunnel={handleDeleteFunnel}
          onUpdate={handleUpdateFunnel}
        />
      )}
    </PageLayout>
  );
};

export default FunnelManagement;